import {
  PAGE, TEXT, BUTTON, IMAGE, VIDEO,
  SECTION, SECTION_COLUMN, CONTAINER, RECTANGLE,
  GROUP, ANCHOR, SHAPE, LINE,
  MENU, MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER,
  FORM, FORM_STEP, FORM_FIELD, FORM_NUMBER, FORM_TEXTAREA, 
  FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX, FORM_SUBMIT,
  OTP_INPUT, OTP_FORM_SUBMIT, OTP_RESEND,
  EXTERNAL_FORM, IFRAME, HTML, CHARTS,
  CONTENT, CONTENT_FILTER, CONTENT_SEARCH, CONTENT_FILTER_CONTAINER,
  CONTENT_LOADING, CONTENT_EMPTY_MESSAGE, CONTENT_PAGINATION_BUTTON,
  CONTENT_CARD, CONTENT_CARD_TEMPLATE,
  ASSET, LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER,
  MODAL, HOTSPOT, POPUP,
  CAROUSEL, CAROUSEL_CARD,
  SEEK, SLIDE,
  BASIC_TEXT, PALETTE_COMPONENT,
  PROMPT_WIDGET, AI_IMAGE_WIDGET,
} from "constants/widgetTypes";

const shortid = require("shortid");

// ============================================================================
// CONSTANTS & TYPE MAPPINGS
// ============================================================================

const WIDGET_TYPE_NAMES = {
  [PAGE]:                     "page",
  [TEXT]:                     "Text",
  [BUTTON]:                   "Button",
  [IMAGE]:                    "Image",
  [VIDEO]:                    "Video",
  [SECTION]:                  "Section",
  [SECTION_COLUMN]:           "Column",
  [CONTAINER]:                "Container",
  [RECTANGLE]:                "Rectangle",
  [GROUP]:                    "Group",
  [ANCHOR]:                   "Anchor",
  [SHAPE]:                    "Shape",
  [LINE]:                     "Line",
  [MENU]:                     "Menu",
  [MENU_CONTAINER]:           "MenuContainer",
  [MENU_DRAGGABLE_CONTAINER]: "MenuContainer",
};

const SUPPORTED_WIDGET_TYPES = new Set(Object.keys(WIDGET_TYPE_NAMES));

const OMIT_WIDGET_TYPES = new Set([
  FORM, FORM_STEP, FORM_FIELD, FORM_NUMBER, FORM_TEXTAREA,
  FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX, FORM_SUBMIT,
  EXTERNAL_FORM, OTP_INPUT, OTP_FORM_SUBMIT, OTP_RESEND,
  IFRAME, HTML, CHARTS,
  CONTENT, CONTENT_FILTER, CONTENT_SEARCH, CONTENT_FILTER_CONTAINER,
  CONTENT_LOADING, CONTENT_EMPTY_MESSAGE, CONTENT_PAGINATION_BUTTON,
  CONTENT_CARD, CONTENT_CARD_TEMPLATE,
  ASSET, LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER,
  MODAL, HOTSPOT, POPUP,
  CAROUSEL, CAROUSEL_CARD,
  SEEK, SLIDE,
  BASIC_TEXT, PALETTE_COMPONENT,
  PROMPT_WIDGET, AI_IMAGE_WIDGET,
  "cleverstory.v2.widgets.form_field",
  "cleverstory.v2.widgets.form_step",
  "cleverstory.v2.widgets.form_input",
  "cleverstory.v2.widgets.form_number",
  "cleverstory.v2.widgets.form_textarea",
  "cleverstory.v2.widgets.form_dropdown",
  "cleverstory.v2.widgets.form_checkbox",
  "cleverstory.v2.widgets.form_boolean",
  "cleverstory.v2.widgets.form_submit",
  "cleverstory.v2.widgets.content_card",
]);

const DEFAULT_MENU_ITEMS = [
  { name: "Home", action: null, children: null },
  { name: "About", action: null, children: null },
  { name: "Gallery", action: null, children: null },
];

// ============================================================================
// GEOMETRY-BASED Z-INDEX ORDERING
// ============================================================================

const OVERLAP_TOLERANCE_PX = 2; // px — rounding slop when comparing rects

const TEXT_WIDGET_TYPE = "cleverstory.v2.widgets.text";

function getWidgetBoundingBox(widget) {
  const layout = widget.properties?.desktop?.layout;
  if (!layout) return null;

  const translate = layout.transform?.translate || "0px,0px";
  const parts = translate.split(",");
  const x = parseFloat(parts[0]) || 0;
  const y = parseFloat(parts[1]) || 0;

  const w = parseFloat(layout.width)  || 0;
  const h = parseFloat(layout.height) || 0;

  // Skip widgets with no meaningful size (%, auto, or 0)
  if (w <= 0 || h <= 0) return null;

  return { x, y, right: x + w, bottom: y + h };
}

/**
 * Returns true when `outer` rect fully contains `inner` rect,
 * i.e. `outer` is visually behind `inner` and acting as its background.
 */
function fullyContains(outer, inner) {
  return (
    outer.x  <= inner.x      + OVERLAP_TOLERANCE_PX &&
    outer.y  <= inner.y      + OVERLAP_TOLERANCE_PX &&
    outer.right  >= inner.right  - OVERLAP_TOLERANCE_PX &&
    outer.bottom >= inner.bottom - OVERLAP_TOLERANCE_PX
  );
}

/**
 * Returns true when the widget is a text widget (must always be on top).
 */
function isTextWidget(widget) {
  return widget.type === TEXT_WIDGET_TYPE;
}

function assignWidgetOrders(widgets) {
  // Group by parent uid
  const byParent = {};
  widgets.forEach((w) => {
    const key = w.parent || "__root__";
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(w);
  });

  Object.values(byParent).forEach((group) => {
    // ── 1. Separate text widgets ─────────────────────────────────────────────
    const textWidgets  = group.filter((w) => isTextWidget(w));
    const otherWidgets = group.filter((w) => !isTextWidget(w));

    // ── 2. Pre-compute bounding boxes ─────────────────────────────────────────
    const boxes = otherWidgets.map((w) => getWidgetBoundingBox(w));

    // ── 3. Score each widget by how many siblings it fully contains ──────────
    //       More containments → further back (lower order)
    const containmentScore = otherWidgets.map((_, i) => {
      if (!boxes[i]) return 0; // no box → keep original position
      let count = 0;
      boxes.forEach((otherBox, j) => {
        if (i !== j && otherBox && fullyContains(boxes[i], otherBox)) {
          count++;
        }
      });
      return count;
    });

    // ── 4. Stable sort: highest containment score first (goes to back) ────────
    const indexed = otherWidgets.map((w, i) => ({
      widget: w,
      score: containmentScore[i],
      originalOrder: w.order,
    }));

    indexed.sort((a, b) => {
      // More background-like (higher score) → lower z-index → comes first
      if (b.score !== a.score) return b.score - a.score;
      // Tie → preserve original DOM traversal order
      return a.originalOrder - b.originalOrder;
    });

    // ── 5. Assign final order values ─────────────────────────────────────────
    //       non-text widgets first, then text widgets pinned on top
    let ord = 1;
    indexed.forEach(({ widget }) => { widget.order = ord++; });
    // Text widgets always get the highest order numbers → rendered on top
    textWidgets
      .sort((a, b) => a.order - b.order) // preserve relative text order
      .forEach((w) => { w.order = ord++; });
  });

  return widgets;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function parseStyleString(styleString) {
  const styles = {};
  styleString.split(";").forEach((rule) => {
    const colonIndex = rule.indexOf(":");
    if (colonIndex === -1) return;
    const key = rule.slice(0, colonIndex).trim();
    const value = rule.slice(colonIndex + 1).trim();
    if (key && value) styles[key] = normalizeStyleValue(value);
  });
  return styles;
}

function parseColor(colorString) {
  if (!colorString || colorString === "transparent" || colorString === "rgba(0, 0, 0, 0)") {
    return { type: "solid", r: 0, g: 0, b: 0, a: 0 };
  }

  const rgbaMatch = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch) {
    return {
      type: "solid",
      r: parseInt(rgbaMatch[1]),
      g: parseInt(rgbaMatch[2]),
      b: parseInt(rgbaMatch[3]),
      a: rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1,
    };
  }

  const rgbMatch = colorString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    return {
      type: "solid",
      r: parseInt(rgbMatch[1]),
      g: parseInt(rgbMatch[2]),
      b: parseInt(rgbMatch[3]),
      a: 1,
    };
  }

  return { type: "solid", r: 0, g: 0, b: 0, a: 1 };
}

function extractImageId(imageUrl) {
  const idMatch = imageUrl.match(/images\/([^\/]+)\//);
  return idMatch ? idMatch[1] : "default_image_id";
}

function getWidgetName(widgetType) {
  return WIDGET_TYPE_NAMES[widgetType] || "Widget";
}

function isOmittedWidget(widgetType) {
  if (!widgetType) return true;
  if (OMIT_WIDGET_TYPES.has(widgetType)) return true;
  const normalized = widgetType.replace(/_/g, '.');
  if (OMIT_WIDGET_TYPES.has(normalized)) return true;
  if (!SUPPORTED_WIDGET_TYPES.has(widgetType)) return true;
  return false;
}

// ============================================================================
// BORDER & STYLE HELPERS
// ============================================================================

function createBorderFromStyle(styleProps) {
  const borderWidth = (styleProps["border-width"] || "0px").replace("px", "");
  const borderColor = styleProps["border-color"] || "rgba(0, 0, 0, 0)";

  const getBorderRadius = (prop) => {
    const value = styleProps[prop] || styleProps["border-radius"] || "0px";
    return value.replace("px", "");
  };

  return {
    sides: null,
    color: parseColor(borderColor),
    width: borderWidth,
    radius: {
      lock: false,
      topLeft: getBorderRadius("border-top-left-radius"),
      topRight: getBorderRadius("border-top-right-radius"),
      bottomLeft: getBorderRadius("border-bottom-left-radius"),
      bottomRight: getBorderRadius("border-bottom-right-radius"),
    },
  };
}

function createShadow(show = false) {
  return {
    color: { type: "solid", r: 255, g: 255, b: 255, a: 1 },
    axes: { x: "0", y: "0", b: "0" },
    show,
  };
}

// ============================================================================
// BACKGROUND HELPERS
// ============================================================================

function createImageBackground(imageUrl, bgColor = null) {
  const imageId = extractImageId(imageUrl);
  return {
    type: "image",
    opacity: 100,
    color: bgColor ? parseColor(bgColor) : { type: "solid", r: 0, g: 0, b: 0, a: 0 },
    scale: "scale-to-fit",
    position: "center",
    media: {
      id: imageId,
      name: "background-image.jpeg",
      contentType: "image/jpeg",
      type: "image",
      width: 1880,
      height: 1253,
      url: imageUrl,
    },
  };
}

function createColorBackground(color) {
  return {
    type: "color",
    color: parseColor(color) || { type: "solid", r: 0, g: 0, b: 0, a: 0 },
  };
}

function createSectionBackground(element, styleProps) {
  const bgColor = styleProps["background-color"];
  const bgImage = styleProps["background-image"];

  if (bgImage && bgImage !== "none") {
    const urlMatch = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
    if (urlMatch) return createImageBackground(urlMatch[1], bgColor);
  }

  return createColorBackground(bgColor);
}

function createColumnBackground(element, styleProps) {
  const bgColor = styleProps["background-color"];
  const bgImage = styleProps["background-image"];

  if (!bgColor && (!bgImage || bgImage === "none")) return null;

  if (bgImage && bgImage !== "none") {
    const urlMatch = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
    if (urlMatch) return createImageBackground(urlMatch[1]);
  }

  return createColorBackground(bgColor);
}

// ============================================================================
// LAYOUT HELPERS
// ============================================================================

function createBaseLayout(styleProps, defaults = {}) {
  return {
    transform: {
      translate: `${styleProps.left || defaults.left || "0px"},${styleProps.top || defaults.top || "0px"}`,
      rotate: styleProps.transform?.match(/rotate\((.*?)\)/)?.[1] || "0deg",
      scale: "1,1",
    },
    width: normalizeStyleValue(styleProps.width || defaults.width || "auto"),
    height: normalizeStyleValue(styleProps.height || defaults.height || "auto"),
  };
}

function createBaseProperties(styleProps, defaults = {}) {
  return {
    visible: true,
    disableOptimize: false,
    layout: createBaseLayout(styleProps, defaults),
    locked: false,
  };
}

function normalizeStyleValue(value) {
  if (!value) return value;
  const match = value.match(/^([+-]?[\d.]+(?:e[+-]?\d+)?)(px|%|em|rem|vh|vw)?$/i);
  if (match) {
    const num = parseFloat(match[1]);
    const unit = match[2] || '';
    if (Math.abs(num) > 10000000) return 'auto';
    return `${Math.round(num)}${unit}`;
  }
  return value;
}

// ============================================================================
// COLUMN CALCULATION HELPERS
// ============================================================================

function calculateColumnDimensions(columnCount) {
  const width = columnCount === 1 ? "100%" : `${100 / columnCount}%`;
  const minWidth = Math.floor(1200 / columnCount);
  return { width, minWidth };
}

// ============================================================================
// MENU PROPERTY HELPERS
// ============================================================================

function createMenuFontStyle(fontSize, color) {
  return {
    style: { bold: false, italic: false, underline: false },
    fontSize: fontSize.replace("px", ""),
    fontFamily: null,
    lineSpacing: null,
    letterSpacing: null,
    color: parseColor(color),
  };
}

function createMenuStyles(element, styleProps) {
  const fontSize = styleProps["font-size"] || "14px";
  const color = styleProps.color || "rgba(255, 255, 255, 1)";
  const bgColor = styleProps["background-color"] || "rgba(0, 183, 202, 1)";

  return {
    menuStyles: {
      font: createMenuFontStyle(fontSize, color),
      background: parseColor(bgColor),
      border: createBorderFromStyle(styleProps),
      shadow: null,
    },
    dropdownColor: parseColor("rgba(255, 255, 255, 1)"),
    dropdownBackground: parseColor(bgColor),
    dropdownBorder: createBorderFromStyle(styleProps),
  };
}

function createMenuHoverStyles(element, styleProps) {
  const fontSize = styleProps["font-size"] || "14px";

  return {
    menuStyles: {
      font: createMenuFontStyle(fontSize, "rgba(0, 0, 0, 1)"),
      background: parseColor("rgba(255, 255, 255, 1)"),
      border: createBorderFromStyle(styleProps),
      shadow: null,
    },
    dropdownColor: parseColor("rgba(255, 255, 255, 1)"),
    dropdownBackground: parseColor("rgba(0, 0, 0, 1)"),
    dropdownBorder: createBorderFromStyle(styleProps),
  };
}

// ============================================================================
// WIDGET-SPECIFIC PROPERTY CREATORS
// ============================================================================

function createPageProperties(element, styleProps) {
  const width = styleProps.width || "1200px";
  const height = styleProps.height || "auto";
  const bgColor = styleProps["background-color"] || "rgba(255, 255, 255, 1)";

  return {
    visible: true,
    disableOptimize: false,
    layout: { transform: null, width, height },
    locked: false,
    container: { minWidth: parseInt(width) || 1200, alignment: null },
    alias: null,
    autoCreateAlias: true,
    background: { type: "color", color: parseColor(bgColor) },
    seo: null,
    social: null,
    twitter: null,
  };
}

function createTextProperties(element, styleProps) {
  let fontSize = "14";
  let color = "rgb(0, 0, 0)";

  const textElement = element.querySelector("p, h1, h2, h3, h4, h5, h6, span");
  if (textElement) {
    if (textElement.style.fontSize) fontSize = textElement.style.fontSize.replace("px", "");
    if (textElement.style.color) color = textElement.style.color;
  }

  const rawStyle = element.getAttribute("style") || "";
  const lineHeightMatch = rawStyle.match(/line-height:\s*([\d.]+)/);
  const letterSpacingMatch = rawStyle.match(/letter-spacing:\s*([\d.]+)(px|em)?/);

  const lineSpacing = lineHeightMatch ? parseFloat(lineHeightMatch[1]) : 1.2;

  let letterSpacing = 0;
  if (letterSpacingMatch) {
    const value = parseFloat(letterSpacingMatch[1]);
    const unit = letterSpacingMatch[2] || 'px';
    if (unit === 'em') {
      letterSpacing = value;
    } else {
      const fontSizeNum = parseFloat(fontSize) || 14;
      letterSpacing = parseFloat((value / fontSizeNum).toFixed(4));
    }
  }

  return {
    fontSize,
    lineSpacing,
    letterSpacing: Math.min(Math.max(letterSpacing, 0), 0),
    color: parseColor(color),
    alignment: styleProps["text-align"] || "left",
    animation: null,
    variables: null,
    llmProperty: null,
  };
}

function createButtonProperties(element, styleProps) {
  const fontSize = (styleProps["font-size"] || "14px").replace("px", "");
  const color = styleProps.color || "rgba(255, 255, 255, 1)";
  const bgColor = styleProps["background-color"] || "rgba(0, 0, 0, 1)";

  const buttonState = {
    font: {
      style: { bold: false, italic: false, underline: false },
      fontSize,
      fontFamily: null,
      lineSpacing: null,
      letterSpacing: null,
      color: parseColor(color),
    },
    background: parseColor(bgColor),
    border: createBorderFromStyle(styleProps),
    shadow: createShadow(false),
  };

  return {
    regular: buttonState,
    hover: {
      ...buttonState,
      font: { ...buttonState.font, color: parseColor(bgColor) },
      background: parseColor(color),
    },
    alignment: null,
    animation: null,
  };
}

function createMediaProperties(styleProps) {
  return {
    crop: null,
    alt: null,
    animation: null,
    border: createBorderFromStyle(styleProps),
    position: null,
    shadow: null,
  };
}

function createSectionProperties(element, styleProps) {
  return {
    stretched: element.getAttribute("data-stretched") === "true" || true,
    margin: parseInt(styleProps.margin) || 0,
    padding: parseInt(styleProps.padding) || 0,
    columnSpacing: parseInt(styleProps["column-gap"] || styleProps.gap) || 0,
    background: createSectionBackground(element, styleProps),
    shadow: createShadow(false),
  };
}

function createSectionColumnProperties(element, styleProps, parentSectionId, sectionColumnMap) {
  const columnCount = sectionColumnMap[parentSectionId] || 1;
  const { minWidth } = calculateColumnDimensions(columnCount);

  return {
    container: { minWidth, alignment: "center" },
    alignment: element.getAttribute("data-alignment") || "center",
    background: createColumnBackground(element, styleProps),
  };
}

function createMenuProperties(styleProps) {
  return {
    stretched: false,
    padding: styleProps.padding || "0",
    container: { minWidth: 1200, alignment: "center" },
    scrollEffect: "NONE",
    background: {
      type: "color",
      color: parseColor(styleProps["background-color"]) || { type: "solid", r: 0, g: 183, b: 202, a: 1 },
      media: null,
      opacity: 0,
      scale: null,
      position: null,
      blur: 0,
    },
    alignment: { position: "center", margin: null },
    verticalAlignment: { position: "center", margin: null },
  };
}

function createMenuContainerProperties(element, styleProps) {
  return {
    regular: createMenuStyles(element, styleProps),
    hover: createMenuHoverStyles(element, styleProps),
    alignment: { position: "center", margin: null },
    trayAlignment: { position: "center", margin: null },
    padding: 0,
    itemHorizontalPadding: 8,
    itemVerticalPadding: 8,
    trayBackground: parseColor("rgba(255, 255, 255, 1)"),
    animation: null,
  };
}

// ============================================================================
// DESKTOP & MOBILE PROPERTY CREATORS
// ============================================================================

function createDesktopProperties(element, widgetType, styleProps, additionalContext = {}) {
  const baseProps = createBaseProperties(styleProps);
  const { parentSectionId, sectionColumnMap } = additionalContext;
  const columnCount = sectionColumnMap?.[parentSectionId] || 1;
  const { width } = calculateColumnDimensions(columnCount);

  const propertyCreators = {
    "cleverstory.v2.widgets.page": () => createPageProperties(element, styleProps),
    "cleverstory.v2.widgets.text": () => ({ ...baseProps, ...createTextProperties(element, styleProps) }),
    "cleverstory.v2.widgets.button": () => ({ ...baseProps, ...createButtonProperties(element, styleProps) }),
    "cleverstory.v2.widgets.image": () => ({ ...baseProps, ...createMediaProperties(styleProps) }),
    "cleverstory.v2.widgets.video": () => ({
      ...baseProps,
      ...createMediaProperties(styleProps),
      autoplay: false,
      loop: false,
      muted: true,
      controls: true,
    }),
    "cleverstory.v2.widgets.rectangle": () => ({
      ...baseProps,
      background: parseColor(styleProps["background-color"]),
      border: createBorderFromStyle(styleProps),
    }),
    "cleverstory.v2.widgets.shape": () => ({
      ...baseProps,
      background: parseColor(styleProps["background-color"]),
      border: createBorderFromStyle(styleProps),
      shadow: null,
      shapeType: element.getAttribute("data-shape-type") || "circle",
    }),
    "cleverstory.v2.widgets.line": () => ({
      ...baseProps,
      color: parseColor(styleProps["border-color"] || styleProps["background-color"]),
      width: (styleProps["border-width"] || "1px").replace("px", ""),
      style: styleProps["border-style"] || "solid",
    }),
    "cleverstory.v2.widgets.section": () => ({ ...baseProps, ...createSectionProperties(element, styleProps) }),
    "cleverstory.v2.widgets.section.column": () => ({
      ...baseProps,
      layout: { transform: { translate: "0px,0px" }, width },
      ...createSectionColumnProperties(element, styleProps, parentSectionId, sectionColumnMap),
    }),
    "cleverstory.v2.widgets.menu": () => ({ ...baseProps, ...createMenuProperties(styleProps) }),
    "cleverstory.v2.widgets.menu.container": () => ({ ...baseProps, ...createMenuContainerProperties(element, styleProps) }),
    "cleverstory.v2.widgets.menu.draggable.container": () => ({ ...baseProps, ...createMenuContainerProperties(element, styleProps) }),
    "cleverstory.v2.widgets.anchor": () => baseProps,
    "cleverstory.v2.widgets.group": () => baseProps,
  };

  const creator = propertyCreators[widgetType];
  return creator ? creator() : baseProps;
}

function createMobileProperties(element, widgetType, styleProps) {
  const baseProps = createBaseProperties(styleProps, { left: "20px", top: "20px" });

  const mobileOverrides = {
    "cleverstory.v2.widgets.page": {
      ...baseProps,
      layout: {
        ...baseProps.layout,
        width: "375px",
        transform: { translate: "0px, 0px", rotate: "0deg", scale: "1,1" },
        height: styleProps.height || "auto",
      },
      container: { minWidth: 375, alignment: null },
    },
    "cleverstory.v2.widgets.section": {
      ...baseProps,
      layout: {
        transform: { translate: "0px, 196.5px", rotate: "0deg" },
        width: "100%",
        height: "auto",
      },
      stretched: false,
      margin: 0,
      padding: 0,
      columnSpacing: 0,
      background: null,
      shadow: createShadow(false),
    },
    "cleverstory.v2.widgets.section.column": {
      ...baseProps,
      layout: {
        transform: { translate: "0px,0px" },
        width: "100%",
        height: "auto",
      },
      container: { minWidth: 375, alignment: null },
      alignment: "center",
      background: null,
    },
  };

  return mobileOverrides[widgetType] || baseProps;
}

// ============================================================================
// CONTENT EXTRACTORS
// ============================================================================

function getPromptId(element) {
  const img = element.querySelector("img");

  const findPromptAttr = (el) => {
    if (!el) return null;
    const attrs = el.attributes;
    for (let i = 0; i < attrs.length; i++) {
      const name = attrs[i].name.toLowerCase().replace(/[-_]/g, '');
      if (name.includes('promptid')) {
        return attrs[i].value || null;
      }
    }
    return null;
  };

  return findPromptAttr(element) || findPromptAttr(img) || null;
}

function extractBackgroundPromptId(element) {
  return getPromptId(element);
}

function extractImageContent(element) {
  const img = element.querySelector("img");
  const promptId = getPromptId(element);
  const src = img?.getAttribute("src");

  if (!src && promptId) {
    return {
      id: "image_id",
      name: img?.getAttribute("alt") || "ai-image.jpg",
      contentType: "image/jpeg",
      type: "image",
      width: parseInt(element.style.width) || 300,
      height: parseInt(element.style.height) || 300,
      url: null,
      brandkitMedia: false,
      promptId,
    };
  }

  if (!src) return null;

  const idMatch = src.match(/images\/([^\/]+)\//);
  const pathMatch = src.match(/\/([^\/]+\/images\/[^?]+)/);

  return {
    id: idMatch ? idMatch[1] : "image_id",
    name: img.getAttribute("alt") || "image.jpg",
    contentType: "image/jpeg",
    type: "image",
    width: parseInt(element.style.width) || 300,
    height: parseInt(element.style.height) || 300,
    url: pathMatch ? pathMatch[1] : src,
    brandkitMedia: false,
    promptId,
  };
}

function extractVideoContent(element) {
  const video = element.querySelector("video");
  if (!video) return null;

  const src = video.getAttribute("src");
  const idMatch = src.match(/videos\/([^\/]+)\//);
  const pathMatch = src.match(/\/([^\/]+\/videos\/[^?]+)/);

  return {
    id: idMatch ? idMatch[1] : "video_id",
    name: "video.mp4",
    contentType: "video/mp4",
    type: "video",
    width: parseInt(element.style.width) || 640,
    height: parseInt(element.style.height) || 360,
    url: pathMatch ? pathMatch[1] : src,
    brandkitMedia: true,
  };
}

function extractMenuContent(element) {
  const menuItems = [];
  const items = element.querySelectorAll("[data-menu-item]");

  items.forEach((item) => {
    menuItems.push({
      uid: item.getAttribute("data-uid") || `widget_${shortid.generate()}`,
      name: item.textContent.trim(),
      action: null,
      children: null,
    });
  });

  if (menuItems.length === 0) {
    return DEFAULT_MENU_ITEMS.map((item) => ({ ...item, uid: `widget_${shortid.generate()}` }));
  }

  return menuItems;
}

function extractContent(element, widgetType) {
  const contentExtractors = {
    "cleverstory.v2.widgets.text": () => element.innerHTML,
    "cleverstory.v2.widgets.button": () => element.querySelector("span")?.textContent || "Button Text",
    "cleverstory.v2.widgets.image": () => extractImageContent(element),
    "cleverstory.v2.widgets.video": () => extractVideoContent(element),
    "cleverstory.v2.widgets.menu.container": () => extractMenuContent(element),
    "cleverstory.v2.widgets.menu.draggable.container": () => extractMenuContent(element),
  };

  const extractor = contentExtractors[widgetType];
  return extractor ? extractor() : null;
}

// ============================================================================
// FONT & ACTION EXTRACTORS
// ============================================================================

function extractFonts(element) {
  const fonts = [];
  const fontMatches = element.innerHTML.match(/cs_font_[a-f0-9]+/g);

  if (fontMatches) {
    fontMatches.forEach((fontId) => {
      const cleanId = fontId.replace("cs_font_", "");
      if (!fonts.includes(cleanId)) fonts.push(cleanId);
    });
  }

  return fonts.length > 0 ? fonts : null;
}

function extractActions(widgetType) {
  return widgetType === "cleverstory.v2.widgets.button" ? [] : null;
}

// ============================================================================
// WIDGET CREATOR
// ============================================================================

function createWidgetFromElement(element, widgetType, widgetId, widgetParent, order, pageWidget, additionalContext = {}) {
  const style = element.getAttribute("style") || "";
  const styleProps = parseStyleString(style);

  let resolvedType = widgetType;
  if (widgetType === "cleverstory.v2.widgets.image") {
    resolvedType = AI_IMAGE_WIDGET;
  }

  const BACKGROUND_WIDGET_TYPES = new Set([
    "cleverstory.v2.widgets.page",
    "cleverstory.v2.widgets.section",
    "cleverstory.v2.widgets.section.column",
  ]);

  let content = extractContent(element, widgetType);

  if (BACKGROUND_WIDGET_TYPES.has(widgetType)) {
    const bgPromptId = extractBackgroundPromptId(element);
    if (bgPromptId) {
      content = { ...(content || {}), backgroundPromptId: bgPromptId };
    }
  }

  return {
    type: resolvedType,
    name: getWidgetName(widgetType),
    uid: widgetId,
    parent: widgetParent,
    order,
    systemDefined: widgetType.includes("section") || widgetType.includes("menu"),
    fonts: extractFonts(element),
    actions: extractActions(widgetType),
    parameter: null,
    pageId: pageWidget.pageId,
    properties: {
      desktop: createDesktopProperties(element, widgetType, styleProps, additionalContext),
      mobile: createMobileProperties(element, widgetType, styleProps),
    },
    content,
  };
}

// ============================================================================
// HTML STRUCTURE VALIDATOR & AUTO-FIXER
// ============================================================================

function findNearestWidgetAncestor(el) {
  let current = el.parentElement;
  while (current) {
    const widgetId = current.getAttribute("data-widgetid");
    if (widgetId) return widgetId;
    current = current.parentElement;
  }
  return null;
}

function validateAndFixHierarchy(doc) {
  const warnings = [];

  doc.querySelectorAll("[data-widgettype]").forEach((el) => {
    const rawType = el.getAttribute("data-widgettype")?.replace(/['"]/g, "");
    const widgetId = el.getAttribute("data-widgetid")?.replace(/['"]/g, "");
    const declaredParentId = el.getAttribute("data-widgetparent")?.replace(/['"]/g, "");

    if (rawType === "cleverstory.v2.widgets.page") return;

    const actualParentId = findNearestWidgetAncestor(el);

    if (!declaredParentId && actualParentId) {
      el.setAttribute("data-widgetparent", actualParentId);
      warnings.push({
        widgetId,
        widgetType: rawType,
        declaredParent: null,
        actualParent: actualParentId,
        issue: `Missing data-widgetparent. Set to DOM parent "${actualParentId}".`,
      });
      return;
    }

    if (actualParentId && actualParentId !== declaredParentId) {
      warnings.push({
        widgetId,
        widgetType: rawType,
        declaredParent: declaredParentId,
        actualParent: actualParentId,
        issue: `Declared parent "${declaredParentId}" does not match actual DOM parent "${actualParentId}". Fixing.`,
      });
      el.setAttribute("data-widgetparent", actualParentId);
    }
  });

  return warnings;
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

export const parseHtmlToWidgets = (html, pageWidget) => {
  const cleanedHtml = html
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\");

  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanedHtml, "text/html");

  const validationWarnings = validateAndFixHierarchy(doc);
  if (validationWarnings.length > 0) {
    console.warn("[parseHtmlToWidgets] Parent mismatches fixed:", validationWarnings);
  }

  const widgets = [];
  let order = 1;
  let pageProperties = null;

  const responsePageWidget = doc.querySelector('[data-widgettype="cleverstory.v2.widgets.page"]');
  const responsePageId = responsePageWidget?.getAttribute("data-widgetid");

  if (responsePageWidget) {
    const styleProps = parseStyleString(responsePageWidget.getAttribute("style") || "");
    pageProperties = {
      desktop: createDesktopProperties(responsePageWidget, "cleverstory.v2.widgets.page", styleProps),
      mobile: createMobileProperties(responsePageWidget, "cleverstory.v2.widgets.page", styleProps),
    };
  }

  const htmlWidgets = doc.querySelectorAll("[data-widgettype]");

  const omittedWidgetIds = new Set();
  doc.querySelectorAll("[data-widgettype]").forEach((el) => {
    const rawType = el.getAttribute("data-widgettype")?.replace(/['"]/g, "");
    const cleanType = rawType?.replace(/['"]/g, "") || "";
    if (isOmittedWidget(cleanType)) {
      const id = el.getAttribute("data-widgetid")?.replace(/['"]/g, "");
      if (id) omittedWidgetIds.add(id);
    }
  });

  function isDescendantOfOmittedWidget(element) {
    let current = element.parentElement;
    while (current) {
      const widgetId = current.getAttribute("data-widgetid")?.replace(/['"]/g, "");
      if (widgetId && omittedWidgetIds.has(widgetId)) return true;
      current = current.parentElement;
    }
    return false;
  }

  // STEP 1: Build old uid → new uid mapping
  const elementUidMap = new WeakMap();
  const uidMapping = {};
  htmlWidgets.forEach((element) => {
    const rawType = element.getAttribute("data-widgettype");
    if (rawType === "cleverstory.v2.widgets.page") return;
    const cleanType = rawType.replace(/['"]/g, "");
    if (isOmittedWidget(cleanType)) return;
    if (isDescendantOfOmittedWidget(element)) return;
  
    const oldId = element.getAttribute("data-widgetid")?.replace(/['"]/g, "");
    if (!oldId) return;
  
    const newId = `widget_${shortid.generate()}`;
    elementUidMap.set(element, newId);
  
    if (!uidMapping[oldId]) {
      uidMapping[oldId] = newId;
    }
  });

  // STEP 2: Build section column map
  const sectionColumnMap = {};
  doc.querySelectorAll('[data-widgettype="cleverstory.v2.widgets.section"]').forEach((section) => {
    const oldSectionId = section.getAttribute("data-widgetid")?.replace(/['"]/g, "");
    const newSectionId = elementUidMap.get(section);
    if (!newSectionId) return;
  
    const columnCount = doc.querySelectorAll(
      `[data-widgettype="cleverstory.v2.widgets.section.column"][data-widgetparent="${oldSectionId}"]`
    ).length;
    sectionColumnMap[newSectionId] = columnCount;
  });

  // STEP 3: Create widgets (order here is just DOM traversal index — will be
  htmlWidgets.forEach((element) => {
    const rawType = element.getAttribute("data-widgettype");
    const widgetId = element.getAttribute("data-widgetid");
    const widgetParent = element.getAttribute("data-widgetparent");

    if (rawType === "cleverstory.v2.widgets.page") return;
    const cleanType = rawType.replace(/['"]/g, "");
    if (isOmittedWidget(cleanType)) return;
    if (isDescendantOfOmittedWidget(element)) return;
  
    const cleanParent = widgetParent?.replace(/['"]/g, "");
  
    const newId = elementUidMap.get(element);
    if (!newId) return;
  
    let newParent;
    if (!cleanParent || cleanParent === "null" || cleanParent === responsePageId) {
      newParent = pageWidget.uid;
    } else {
      newParent = uidMapping[cleanParent] || pageWidget.uid;
    }
  
    const widget = createWidgetFromElement(
      element, cleanType, newId, newParent, order, pageWidget,
      { parentSectionId: newParent, sectionColumnMap }
    );
  
    if (widget) {
      widgets.push(widget);
      order++;
    }
  });

  // STEP 4: Re-order widgets within each parent group based on type priority.
  assignWidgetOrders(widgets);

  return { widgets, pageProperties };
};